import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Scale, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { api } from "../lib/api";
import Seo from "../components/Seo";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Takedown() {
  const [form, setForm] = useState({
    reporter_name: "",
    reporter_email: "",
    reporter_role: "",
    infringing_url: "",
    infringing_description: "",
    original_work_description: "",
    good_faith_statement: false,
    accuracy_statement: false,
    signature: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/takedown", form);
      setReceipt(data);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (receipt) {
    return (
      <div className="grain-bg min-h-screen bg-zinc-950 text-white p-6 md:p-12">
        <div className="max-w-2xl mx-auto pt-16">
          <div className="border border-emerald-500/20 bg-emerald-500/5 p-8">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-3 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> Received
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mb-4">
              Your takedown notice is logged.
            </h1>
            <p className="text-sm text-zinc-300 leading-relaxed mb-6">
              We received your notice and will review it promptly. Reference:
              <span className="font-mono text-emerald-400 ml-2" data-testid="takedown-id">
                {receipt.takedown_id}
              </span>
            </p>
            <p className="text-xs text-zinc-500 leading-relaxed mb-8">
              We may contact you at the email you provided if we need additional information.
              You should preserve any evidence and consult legal counsel for further action.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-white border border-zinc-800 hover:bg-zinc-800 px-4 py-2 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4" /> Back to LinguaSub
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white p-6 md:p-12">
      <Seo path="/takedown" title="Copyright Takedown" description="Report content on LinguaSub that infringes your copyright. DMCA-style notice with good-faith and accuracy statements." />
      <div className="max-w-3xl mx-auto pt-8 md:pt-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white mb-8 transition-colors"
          data-testid="takedown-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>

        <div className="mb-10">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3 flex items-center gap-1.5">
            <Scale className="w-3 h-3" /> DMCA / Copyright takedown
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mb-4">
            Report infringing content
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">
            If you believe content processed on LinguaSub infringes your rights, submit a notice
            below. We investigate all valid notices and act on them promptly. Filing false or
            bad-faith reports carries legal consequences.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="takedown-form">
          <FieldGroup label="Your name" required>
            <Input value={form.reporter_name} onChange={set("reporter_name")} required maxLength={120} data-testid="td-name" className="input-dark" />
          </FieldGroup>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FieldGroup label="Your email" required>
              <Input type="email" value={form.reporter_email} onChange={set("reporter_email")} required data-testid="td-email" className="input-dark" />
            </FieldGroup>
            <FieldGroup label="Your role" required hint="Rights holder, agent, lawyer…">
              <Input value={form.reporter_role} onChange={set("reporter_role")} required maxLength={120} data-testid="td-role" className="input-dark" />
            </FieldGroup>
          </div>

          <FieldGroup label="URL or reference of the allegedly infringing subtitle/job" hint="Optional — a link, job_id, or screenshot description">
            <Input value={form.infringing_url} onChange={set("infringing_url")} maxLength={1000} data-testid="td-url" className="input-dark" />
          </FieldGroup>

          <FieldGroup label="Describe the infringing content" required>
            <Textarea value={form.infringing_description} onChange={set("infringing_description")} required minLength={10} maxLength={4000} rows={4} data-testid="td-infringing" className="textarea-dark" />
          </FieldGroup>

          <FieldGroup label="Describe your original work and how you hold the rights" required>
            <Textarea value={form.original_work_description} onChange={set("original_work_description")} required minLength={10} maxLength={4000} rows={4} data-testid="td-original" className="textarea-dark" />
          </FieldGroup>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="td-goodfaith-label">
            <Checkbox checked={form.good_faith_statement} onCheckedChange={(v) => setForm((f) => ({ ...f, good_faith_statement: !!v }))} data-testid="td-goodfaith" className="mt-0.5 border-zinc-700 data-[state=checked]:bg-white data-[state=checked]:text-zinc-950" />
            <span className="text-xs text-zinc-300 leading-relaxed">
              I have a good faith belief that the use of the material described above is not
              authorised by the rights holder, its agent, or the law.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="td-accuracy-label">
            <Checkbox checked={form.accuracy_statement} onCheckedChange={(v) => setForm((f) => ({ ...f, accuracy_statement: !!v }))} data-testid="td-accuracy" className="mt-0.5 border-zinc-700 data-[state=checked]:bg-white data-[state=checked]:text-zinc-950" />
            <span className="text-xs text-zinc-300 leading-relaxed">
              The information in this notice is accurate and, under penalty of perjury, I am the
              rights holder or authorised to act on their behalf.
            </span>
          </label>

          <FieldGroup label="Signature" required hint="Type your full legal name">
            <Input value={form.signature} onChange={set("signature")} required maxLength={120} data-testid="td-signature" className="input-dark" />
          </FieldGroup>

          {error && (
            <div data-testid="td-error" className="text-xs font-mono text-red-400 border border-red-500/20 bg-red-500/5 px-3 py-2">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="td-submit"
            className="w-full md:w-auto h-11 px-8 bg-white text-zinc-950 hover:bg-zinc-200 disabled:bg-zinc-800 disabled:text-zinc-500 font-semibold rounded-md transition-colors duration-200"
          >
            {submitting ? "Submitting..." : "Submit takedown notice"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function FieldGroup({ label, required, hint, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
        <span>{label}{required && <span className="text-red-400 ml-1">*</span>}</span>
        {hint && <span className="text-zinc-600 normal-case tracking-normal">— {hint}</span>}
      </div>
      {children}
    </div>
  );
}
